import {
  ApplicationRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
  signal,
  ViewChild
} from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { DxHtmlEditorComponent } from "devextreme-angular";
import { ArrayStore, CustomStore, DataSource } from "devextreme/common/data";
import {
  ContentReadyEvent,
  dxHtmlEditorToolbar,
  HtmlEditorPredefinedToolbarItem
} from "devextreme/ui/html_editor";
import { lastValueFrom } from "rxjs";
import { ReminderTemplateBackend } from "src/app/backend/api/reminder-template.backend";
import { CampaignTemplate } from "src/app/backend/models/campaign-template";
import { MessageTemplateType } from "src/app/backend/models/message-template";
import { sntConfirm } from "src/app/common/dialog";
import { TinyEditorDirective } from "src/app/components/ui/wysiwyg-editor/tiny-editor.directive";
import { wrapTinyEditorContentWithToxStyles } from "src/app/components/ui/wysiwyg-editor/tox-checklist-export-styles";
import { blockWysiwygTemplateMount } from "src/app/modules/bootstrap/pms-ui/blocks/wysiwyg-block/wysiwyg-template.block";
import { RegionService } from "src/app/services/feature-access-control/region.service";
import { blobToDataUrl } from "src/app/support/blob";
import { sntTranslate } from "src/app/support/translate";
import { EditorOptions as TinyEditorOptions } from "tinymce";

export enum WysiwygEngine {
  DEVX = "devx",
  TINY = "tiny"
}

type WysiwygToolbarType =
  | "small"
  | "signature"
  | "template-editor"
  | "default"
  | false;

@Component({
  standalone: false,
  selector: "snt-wysiwyg-editor",
  templateUrl: "wysiwyg-editor.component.html",
  styleUrl: "wysiwyg-editor.component.scss",
  host: {
    "[class.wysiwyg-highlight-on-white]": "highlightOnWhite()"
  }
})
export class WysiwygEditorComponent {
  @ViewChild(TinyEditorDirective)
  tinyEditorDirective: TinyEditorDirective | undefined;

  @ViewChild(DxHtmlEditorComponent)
  devxHtmlEditorComponent: DxHtmlEditorComponent | undefined;

  @ViewChild("target")
  targetRef: ElementRef<HTMLElement> | undefined;

  @Input()
  initialContent: string | undefined;

  @Input()
  mergeTagsList: any | undefined;

  @Input()
  containerMode: "detached" | "inside" = "detached";

  @Input()
  maxImageSize?: number;

  @Input()
  engine: WysiwygEngine = WysiwygEngine.TINY;

  @Input()
  placeholder: string = "";

  @Input()
  readOnly: boolean = false;

  enableTemplates = input<boolean>(false);

  toolbar = input<WysiwygToolbarType>("default");
  devxEngineMinimal = input<boolean>(false);
  /** When true with DEVX engine, draws a visible border so the editor stands out on white backgrounds. */
  highlightOnWhite = input<boolean>(false);

  variablesDataSource = input<string[] | undefined>(undefined);
  variablesEscapeChar = input<string[] | undefined>(undefined);
  variablesEnabled = computed<boolean>(() => {
    return !!this.variablesDataSource() && !!this.variablesEscapeChar();
  });

  @Output()
  contentChanged: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  loadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  tinyLoading = signal<boolean>(false);

  private tinyInitialContentApplied = false;

  tinyEditorOptions = computed<Partial<TinyEditorOptions> | undefined>(() => {
    const toolbar = this.toolbar();

    const tinyEditorOptions: Partial<TinyEditorOptions> = {
      menubar: false,
      plugins:
        "preview casechange importcss tinydrive searchreplace autolink save directionality advcode visualblocks visualchars fullscreen image link media mediaembed template codesample table charmap pagebreak nonbreaking anchor tableofcontents insertdatetime advlist lists checklist wordcount editimage help formatpainter permanentpen pageembed charmap quickbars emoticons advtable export mergetags".split(
          " "
        ),
      toolbar:
        "undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | forecolor backcolor casechange permanentpen formatpainter removeformat | pagebreak | charmap emoticons | fullscreen  preview | pageembed template link anchor | mergetags | spellchecker",
      toolbar_mode: "wrap",
      spellchecker_language: "it",
      mergetags_prefix: "{*",
      mergetags_suffix: "}",
      mergetags_list: this.mergeTagsList,
      height: "100%",
      language: this.regionService.get(),
      content_langs: [{ title: "Italiano", code: "it" }],
      resize: false,
      font_size_formats: "6pt 7pt 8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt",
      images_upload_handler: async (blobInfo, progress) => {
        return blobToDataUrl(blobInfo.blob());
      }
    };

    switch (toolbar) {
      case "small": {
        tinyEditorOptions.toolbar =
          "bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | forecolor backcolor formatpainter removeformat";
        break;
      }
      case "signature": {
        tinyEditorOptions.toolbar =
          "bold italic underline strikethrough | fontfamily fontsize | alignleft aligncenter alignright | bullist | forecolor backcolor | emoticons";
        break;
      }
      case "template-editor": {
        tinyEditorOptions.toolbar =
          "bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | removeformat | mergetags signature";
        break;
      }
      case "default": {
        tinyEditorOptions.toolbar =
          "bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | forecolor backcolor formatpainter removeformat";
        break;
      }
      case false: {
        tinyEditorOptions.toolbar = false;
        break;
      }
    }

    return tinyEditorOptions;
  });

  devxEditorToolbar = computed<dxHtmlEditorToolbar>(() => {
    const toolbar = this.toolbar();
    const devxEngineMinimal = this.devxEngineMinimal();
    const enableTemplates = this.enableTemplates();
    const variablesEnabled = this.variablesEnabled();

    const dxToolbar: dxHtmlEditorToolbar = {
      items: [
        {
          name: "size",
          acceptedValues: [
            "8pt",
            "10pt",
            "12pt",
            "14pt",
            "18pt",
            "24pt",
            "36pt"
          ]
        },
        {
          name: "font",
          acceptedValues: [
            "Arial",
            "Courier New",
            "Georgia",
            "Impact",
            "Lucida Console",
            "Tahoma",
            "Times New Roman",
            "Verdana"
          ]
        },
        ...(<HtmlEditorPredefinedToolbarItem[]>[
          "separator",
          "bold",
          "italic",
          "strike",
          "underline",
          "separator",
          "alignLeft",
          "alignCenter",
          "alignRight",
          "alignJustify",
          "separator",
          "orderedList",
          "bulletList",
          "separator",
          "insertTable",
          "deleteTable",
          "insertRowAbove",
          "insertRowBelow",
          "deleteRow",
          "insertColumnLeft",
          "insertColumnRight",
          "deleteColumn"
        ])
      ].filter((x: any) => {
        if (devxEngineMinimal && x.name === "font") {
          return false;
        }

        return true;
      })
    };

    if (variablesEnabled) {
      dxToolbar.items?.splice(0, 0, {
        widget: "dxDropDownButton",
        options: {
          text: sntTranslate(marker("Variabili")),
          icon: "fa-light fa-hashtag",
          displayExpr: "name",
          valueExpr: "id",
          focusStateEnabled: false,
          dataSource: new DataSource({
            paginate: false,
            store: new ArrayStore({
              data:
                this.variablesDataSource()?.map((x: string) => {
                  return {
                    id: x,
                    name: sntTranslate(`variable-${x}`)
                  };
                }) || []
            })
          }),
          itemTemplate: (
            data: { id: string; name: string },
            index: number,
            element: HTMLElement
          ) => {
            element.innerHTML = data.name;
          },
          dropDownOptions: {
            width: 220
          },
          onSelectionChanged: ({
            item
          }: {
            item: { id: string; name: string };
          }) => {
            const quill =
              this.devxHtmlEditorComponent?.instance.getQuillInstance();

            const selection = quill.getSelection(true);
            const caretPosition = selection
              ? selection.index
              : quill.getLength();

            const variableData = {
              value: item.id,
              escapeChar: this.variablesEscapeChar() || ["{{", "}}"]
            };

            // HACK setTimeout necessario per non perdere il focus dell'edito
            // non so il motivo, visto nel codice di DevExtreme
            setTimeout(() => {
              quill.insertEmbed(caretPosition, "variable", variableData);
              quill.setSelection(caretPosition + 1);
            }, 0);
          }
        }
      });
    }

    if (enableTemplates) {
      dxToolbar.items?.splice(0, 0, {
        widget: "dxDropDownButton",
        options: {
          text: sntTranslate(marker("Template")),
          icon: "fa-light fa-envelope-open-text",
          displayExpr: "name",
          valueExpr: "id",
          focusStateEnabled: false,
          dataSource: new DataSource({
            paginate: false,
            store: new CustomStore({
              load: async () => {
                return await lastValueFrom(
                  this.reminderTemplateBackend.listAllByType(
                    MessageTemplateType.CAMPAIGN
                  )
                );
              }
            })
          }),
          itemTemplate: (
            data: CampaignTemplate,
            index: number,
            element: HTMLElement
          ) => {
            blockWysiwygTemplateMount({
              element,
              applicationRefInjector: this.applicationRef.injector,
              template: data
            });
          },
          dropDownOptions: {
            width: 220
          },
          onSelectionChanged: ({ item }: { item: CampaignTemplate }) => {
            this.setContent(item.content);
          }
        }
      });
    }

    return dxToolbar;
  });

  private readonly regionService = inject(RegionService);
  private readonly reminderTemplateBackend = inject(ReminderTemplateBackend);
  private readonly applicationRef = inject(ApplicationRef);

  constructor() {
    if (this.engine === WysiwygEngine.TINY) {
      this.setTinyLoading(true);
    }
  }

  onTinyEditorReady(): void {
    if (!this.tinyInitialContentApplied) {
      this.applyTinyInitialContent();
      this.tinyInitialContentApplied = true;
    }
  }

  onTinyEditorRendered(): void {
    this.setTinyLoading(false);
  }

  deactivated(): void {
    if (this.engine === WysiwygEngine.TINY) {
      this.setTinyLoading(true);
    }

    this.tinyEditorDirective?.decativated();
  }

  activated(): void {
    this.tinyEditorDirective?.activated();
  }

  destroied(): void {
    this.tinyEditorDirective?.destroied();
  }

  getContent(skipToxStyle?: boolean): string | undefined {
    switch (this.engine) {
      case WysiwygEngine.DEVX: {
        return this.devxHtmlEditorComponent?.value || "";
      }
      case WysiwygEngine.TINY: {
        const editorContent = this.tinyEditorDirective?.getContent() || "";

        if (skipToxStyle) {
          return editorContent;
        }

        return wrapTinyEditorContentWithToxStyles(editorContent);
      }
    }
  }

  async setContent(content: string): Promise<void> {
    const result = await sntConfirm(
      sntTranslate(marker("Editor")),
      sntTranslate(
        marker(
          "Proseguendo il contenuto dell'editor verrà sovrascritto e perderei eventuali modifiche, confermi?"
        )
      ),
      {
        confirmText: sntTranslate(marker("Si")),
        cancelText: sntTranslate(marker("No"))
      }
    );

    if (result) {
      this.devxHtmlEditorComponent?.instance.option("value", content);
    }
  }

  onDevxContentReady(dxEvent: ContentReadyEvent): void {
    const quill = dxEvent.component.getQuillInstance();
    quill.uploader.preventImageUpload = true;

    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node: any, delta: any) => {
      delta.ops = delta.ops.map((op: any) => {
        return {
          insert: op.insert
        };
      });

      return delta;
    });
  }

  private applyTinyInitialContent(): void {
    const content = this.initialContent ?? "";

    this.tinyEditorDirective?.setContent(content);
  }

  private setTinyLoading(loading: boolean): void {
    this.tinyLoading.set(loading);
    this.loadingChange.emit(loading);
  }
}
